import React, { useEffect, useState } from 'react';
import {
  Button, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions,
  DialogContentText, MenuItem, TextField, Alert,
} from '@mui/material';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { useAuth } from '../../contexts/AuthContext';
import { CameraUploadImage } from '../../api/cameraUpload';
import { listAnnotatorProjects, pushImageToTraining, PushResult } from '../../api/cameraUploadTrainingPush';
import type { AnnotatorProject } from '../../types/annotator';

interface PushToTrainingDataButtonProps {
  deviceId: string;
  image: CameraUploadImage;
}

// Admin-only: pushes this image + its boxed classifications into an
// Annotator project as training data, so corrections made here can inform a
// retrain of whichever model produced them.
const PushToTrainingDataButton: React.FC<PushToTrainingDataButtonProps> = ({ deviceId, image }) => {
  const auth = useAuth();
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState<AnnotatorProject[]>([]);
  const [projectId, setProjectId] = useState<number | ''>('');
  const [loadingProjects, setLoadingProjects] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<PushResult | null>(null);

  useEffect(() => {
    if (!open) return;
    setLoadingProjects(true);
    setError('');
    setResult(null);
    listAnnotatorProjects()
      .then((data) => {
        setProjects(data);
        if (data.length > 0) setProjectId(data[0].id);
      })
      .catch((err: any) => setError(err?.message || 'Failed to load Annotator projects'))
      .finally(() => setLoadingProjects(false));
  }, [open]);

  if (!auth.hasRole('DMO_ADMIN')) return null;

  const handlePush = async () => {
    const project = projects.find((p) => p.id === projectId);
    if (!project) return;
    setPushing(true);
    setError('');
    try {
      const pushResult = await pushImageToTraining(deviceId, image, project, auth.tokens?.accessToken ?? '');
      setResult(pushResult);
    } catch (err: any) {
      setError(err?.message || 'Failed to push to training data');
    } finally {
      setPushing(false);
    }
  };

  return (
    <>
      <Button variant="outlined" size="small" startIcon={<ModelTrainingIcon />} onClick={() => setOpen(true)}>
        Add to training data
      </Button>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Push to Annotator training data</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Uploads "{image.originalFileName}" and its boxed classifications into an Annotator project so they can be
            used to retrain a model. Classifications with no drawn box are skipped.
          </DialogContentText>

          {loadingProjects ? (
            <CircularProgress size={24} />
          ) : (
            <TextField
              select
              label="Annotator project"
              value={projectId}
              onChange={(e) => setProjectId(Number(e.target.value))}
              fullWidth
              size="small"
              disabled={pushing || !!result}
            >
              {projects.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
              ))}
            </TextField>
          )}

          {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
          {result && (
            <Alert severity="success" sx={{ mt: 2 }}>
              Pushed classes: {result.pushedClasses.join(', ') || 'none'}.
              {result.skippedNoBox.length > 0 && ` Skipped (no box): ${result.skippedNoBox.join(', ')}.`}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>{result ? 'Close' : 'Cancel'}</Button>
          {!result && (
            <Button variant="contained" onClick={handlePush} disabled={pushing || !projectId}>
              {pushing ? <CircularProgress size={20} /> : 'Push'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PushToTrainingDataButton;
